import React from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface BillingAddress {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postal_code: string;
    country: string;
}

interface StripePaymentFormProps {
    saveCard: boolean;
    onSaveCardChange: (checked: boolean) => void;
    onValidityChange: (isValid: boolean) => void;
    billingAddress: BillingAddress;
    onBillingAddressChange: (address: BillingAddress) => void;
    taxId: string;
    onTaxIdChange: (taxId: string) => void;
}

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#32325d',
            fontFamily: 'Arial, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#fa755a',
            iconColor: '#fa755a',
        },
    },
    hidePostalCode: true,
};

const StripePaymentForm = ({ 
    saveCard, 
    onSaveCardChange, 
    onValidityChange,
    billingAddress,
    onBillingAddressChange,
    taxId,
    onTaxIdChange
}: StripePaymentFormProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const [cardComplete, setCardComplete] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    // Validate form completeness
    const isFormValid = React.useMemo(() => {
        return cardComplete && 
               !error && 
               billingAddress.line1.trim() !== '' &&
               billingAddress.city.trim() !== '' &&
               billingAddress.state.trim() !== '' &&
               billingAddress.postal_code.trim() !== '' &&
               billingAddress.country.trim() !== '' &&
               taxId.trim() !== '';
    }, [cardComplete, error, billingAddress, taxId]);

    // Remove onValidityChange from dependencies to prevent infinite loop
    React.useEffect(() => {
        onValidityChange(isFormValid);
    }, [isFormValid]);

    const handleCardChange = (event: any) => {
        setError(event.error ? event.error.message : '');
        setCardComplete(event.complete);
    };

    const handleAddressChange = (field: keyof BillingAddress, value: string) => {
        onBillingAddressChange({
            ...billingAddress,
            [field]: value
        });
    };

    // Australian states
    const australianStates = [
        { value: 'NSW', label: 'New South Wales' },
        { value: 'VIC', label: 'Victoria' },
        { value: 'QLD', label: 'Queensland' },
        { value: 'WA', label: 'Western Australia' },
        { value: 'SA', label: 'South Australia' },
        { value: 'TAS', label: 'Tasmania' },
        { value: 'ACT', label: 'Australian Capital Territory' },
        { value: 'NT', label: 'Northern Territory' }
    ];

    return (
        <div className="space-y-6">
            <div>
                <Label htmlFor="card-element" className="text-base font-medium mb-2 block">
                    Card Details <span className="text-red-500">*</span>
                </Label>
                <div className="p-4 border rounded-md bg-white">
                    <CardElement id="card-element" options={CARD_ELEMENT_OPTIONS} onChange={handleCardChange} />
                </div>
                {error && <div className="text-red-500 text-sm mt-2">{error}</div>}
            </div>

            {/* Billing Address Section */}
            <div className="space-y-4">
                <Label className="text-base font-medium">
                    Billing Address <span className="text-red-500">*</span>
                </Label>
                
                <div>
                    <Label htmlFor="address-line1" className="text-sm mb-1 block">
                        Address Line 1 <span className="text-red-500">*</span>
                    </Label>
                    <Input
                        id="address-line1"
                        type="text"
                        placeholder="Street address"
                        value={billingAddress.line1}
                        onChange={(e) => handleAddressChange('line1', e.target.value)}
                        className="w-full"
                    />
                </div>

                <div>
                    <Label htmlFor="address-line2" className="text-sm mb-1 block">
                        Address Line 2
                    </Label>
                    <Input
                        id="address-line2"
                        type="text"
                        placeholder="Apartment, suite, etc. (optional)"
                        value={billingAddress.line2 || ''}
                        onChange={(e) => handleAddressChange('line2', e.target.value)}
                        className="w-full"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="city" className="text-sm mb-1 block">
                            City <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="city"
                            type="text"
                            placeholder="City"
                            value={billingAddress.city}
                            onChange={(e) => handleAddressChange('city', e.target.value)}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <Label htmlFor="state" className="text-sm mb-1 block">
                            State <span className="text-red-500">*</span>
                        </Label>
                        <Select
                            value={billingAddress.state}
                            onValueChange={(value) => handleAddressChange('state', value)}
                        >
                            <SelectTrigger id="state" className="w-full">
                                <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                            <SelectContent>
                                {australianStates.map((state) => (
                                    <SelectItem key={state.value} value={state.value}>
                                        {state.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label htmlFor="postal-code" className="text-sm mb-1 block">
                            Postcode <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="postal-code"
                            type="text"
                            placeholder="Postcode"
                            value={billingAddress.postal_code}
                            onChange={(e) => handleAddressChange('postal_code', e.target.value)}
                            className="w-full"
                        />
                    </div>

                    <div>
                        <Label htmlFor="country" className="text-sm mb-1 block">
                            Country <span className="text-red-500">*</span>
                        </Label>
                        <Input
                            id="country"
                            type="text"
                            value={billingAddress.country}
                            disabled
                            className="w-full bg-gray-50"
                        />
                    </div>
                </div>
            </div>

            {/* ABN Section */}
            <div>
                <Label htmlFor="abn" className="text-base font-medium mb-2 block">
                    Australian Business Number (ABN) <span className="text-red-500">*</span>
                </Label>
                <Input
                    id="abn"
                    type="text"
                    placeholder="Enter your 11-digit ABN"
                    value={taxId}
                    onChange={(e) => onTaxIdChange(e.target.value.replace(/\s/g, ''))}
                    maxLength={11}
                    className="w-full"
                />
                <p className="text-sm text-gray-500 mt-1">
                    Required for GST calculation. Enter 11 digits without spaces.
                </p>
            </div>

            <div className="flex items-center justify-between">
                <div>
                    <div className="font-medium">Store card for future billing?</div>
                    <div className="text-sm text-gray-500">
                        If you need more info, please check budget planning
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Switch
                        id="save-card"
                        checked={saveCard}
                        onCheckedChange={onSaveCardChange}
                    />
                    <Label
                        htmlFor="save-card"
                        className="text-sm text-gray-600 cursor-pointer"
                    >
                        Store Card
                    </Label>
                </div>
            </div>
        </div>
    );
};

export default StripePaymentForm;
